import { ProductMapper } from "@omni/mappers/product/product.mapper";
import { ProductSliderSources } from "@omni/types/components/product/enums/productSliderResource.enum";
import { Button } from "@omni/ui/components/Button/button.component";

import { ProductSlider } from "@/components/Product/ProductSlider/productSlider.container";
import AutocompleteHeader from "@/components/Search/SearchBar/AutocompleteItem/autocompleteHeader.container";
import { ProductsItem } from "@/components/Search/SearchBar/Products/products.item.component";
import { BaseTemplateProps } from "@/components/Search/SearchBar/ResultsPanel/resultsPanel.templates";
import { ProductsSourceType } from "@/components/Search/SearchBar/searchBar.types";
import { SearchHitContextProvider } from "@/components/Search/SearchResults/StateComponents/ResultList/context/searchHit.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { PRODUCT_COUNT } from "../searchBar.config";
import "./products.scss";

type ProductsTemplateProps = BaseTemplateProps<ProductsSourceType>;

const ProductsTemplate = ({
    items,
    autocompleteState,
    autocompleteActions
}: ProductsTemplateProps) => {
    const { t } = useGranite();

    const handleShowAllProductsClick = () => {
        if (!autocompleteState?.query) return;

        autocompleteActions?.submit(autocompleteState?.query);
    };

    const getShowAllProductsHeader = () => {
        const amountOfProducts = autocompleteState?.context.nbProductHits as
            | number
            | undefined;

        if (!amountOfProducts || autocompleteState?.query?.length === 0) return;

        return (
            <Button
                type={"button"}
                color={"link"}
                className={"py-2 px-0 text-primary"}
                onClick={handleShowAllProductsClick}
            >
                {t("searchBox.panel.products.header.showAll", amountOfProducts)}
            </Button>
        );
    };

    const headerTitle =
        autocompleteState?.query?.length === 0
            ? t("searchBox.panel.products.header.title.emptyQuery")
            : t("searchBox.panel.products.header.title.querying");

    return (
        <div>
            <AutocompleteHeader headerTitle={headerTitle}>
                {getShowAllProductsHeader()}
            </AutocompleteHeader>

            <div className={"productsTemplate__items--slider d-lg-none"}>
                <ProductSlider
                    title={""}
                    source={ProductSliderSources.HandPicked}
                    skus={items.map(({ sku }) => sku)}
                    productTileConfig={{
                        showButton: false,
                        showCategoryLink: false,
                        useInsidePadding: false,
                        amountOfFeatures: 0,
                        allowInstantAddToCart: false
                    }}
                    prefetchedProducts={items.map(
                        ProductMapper.fromAlgoliaSearchAutocompleteToProductTile
                    )}
                    breakpointsConfig={{
                        layoutSettings: {
                            xs: { slidesPerGroup: 2.0, slidesPerView: 2.2 },
                            sm: { slidesPerGroup: 2.0, slidesPerView: 2.2 },
                            md: { slidesPerGroup: 3.0, slidesPerView: 3.0 },
                            lg: { slidesPerGroup: 4.0, slidesPerView: 4.0 },
                            xl: { slidesPerGroup: 6.0, slidesPerView: 6.0 }
                        },
                        hidden: []
                    }}
                />
            </div>

            <ul className={"productsTemplate__items--grid list-unstyled mb-0"}>
                {items.slice(0, PRODUCT_COUNT).map((item) => (
                    <li key={item.sku}>
                        <SearchHitContextProvider
                            queryID={item._queryID}
                            position={item._position}
                        >
                            <ProductsItem
                                item={item}
                                autocompleteState={autocompleteState}
                                autocompleteActions={autocompleteActions}
                            />
                        </SearchHitContextProvider>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ProductsTemplate;
